// Skeleton placeholders. Reaching for skeletons instead of a spinner is part of
// Step 4 polish — nobody likes a tiny gray dot, especially a 7-year-old.
//
// All shapes use the warm-bg palette so they fade into the home/login surfaces
// without flashing a different color while data loads.

import type { CSSProperties } from 'react'

interface BoxProps {
  className?: string
  style?: CSSProperties
  /** width as Tailwind class (e.g. "w-32") */
  w?: string
  /** height as Tailwind class (e.g. "h-6") */
  h?: string
}

function Box({ w = 'w-full', h = 'h-4', className = '', style }: BoxProps) {
  return (
    <div
      style={style}
      className={`${w} ${h} animate-pulse rounded-[var(--radius-soft)] bg-[var(--color-muted)]/15 ${className}`}
    />
  )
}

/** Centered "page is loading" state — avoids layout jump when content lands. */
export function PageSkeleton() {
  return (
    <main
      className="mx-auto flex min-h-full max-w-2xl flex-col gap-6 p-8"
      aria-busy="true"
      aria-live="polite"
    >
      <header className="flex items-baseline justify-between">
        <div className="flex flex-col gap-2">
          <Box w="w-12" h="h-3" />
          <Box w="w-40" h="h-9" />
        </div>
        <Box w="w-12" h="h-4" />
      </header>
      <Box w="w-32" h="h-3" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <CardSkeleton variant="joseph" />
        <CardSkeleton variant="nativity" />
      </div>
    </main>
  )
}

export function CardSkeleton({ variant }: { variant: 'joseph' | 'nativity' }) {
  const border =
    variant === 'joseph'
      ? 'border-[var(--color-joseph-primary)]/30 bg-[var(--color-joseph-bg)]'
      : 'border-[var(--color-nativity-primary)]/30 bg-[var(--color-nativity-bg)]'
  return (
    <div className={`flex flex-col gap-3 rounded-[var(--radius-soft)] border-2 p-5 ${border}`}>
      <Box w="w-32" h="h-6" />
      <Box w="w-24" h="h-4" />
      <Box w="w-40" h="h-3" className="mt-2" />
    </div>
  )
}

/** Compact skeleton for the /k/:token welcome moment before the user data lands. */
export function WelcomeSkeleton() {
  return (
    <main
      className="mx-auto flex min-h-full max-w-md flex-col items-center justify-center gap-3 p-8"
      aria-busy="true"
      aria-live="polite"
    >
      <Box w="w-48" h="h-9" />
      <Box w="w-32" h="h-4" />
    </main>
  )
}
